import { supabase, studioRedirectUrl } from './supabase';
import { readErrorText } from './cloudContent';

export type TeamRole = 'owner' | 'manager';

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: TeamRole;
  addedAt: string;
}

export interface StudioSession {
  memberId: string;
  name: string;
  role: TeamRole;
  signedInAt: string;
  expiresAt: number;
}

export const teamStorageKey = 'artistos-team-members';
export const sessionStorageKey = 'artistos-studio-session';

const setupSessionTtlMs = 24 * 60 * 60 * 1000;

export const defaultTeam: TeamMember[] = [
  {
    id: 'jhowington@sfmc.com',
    name: 'Joey Howington',
    email: 'jhowington@sfmc.com',
    phone: '8173430735',
    role: 'owner',
    addedAt: new Date().toISOString(),
  },
  {
    id: 'admin@coopertyband.com',
    name: 'Cooper Ty Band',
    email: 'admin@coopertyband.com',
    phone: '8174085369',
    role: 'owner',
    addedAt: new Date().toISOString(),
  },
];

/**
 * Merges anyone hard-coded above into a fetched list, so a database hiccup or a
 * mis-edited access list can never lock an owner out of the published studio.
 */
function withDefaultOwners(members: TeamMember[]): TeamMember[] {
  const builtIn = new Set(defaultTeam.map((owner) => owner.email));
  // A stale or mis-edited list can never demote a built-in owner, so the owner
  // role always comes from the code rather than from whatever was stored.
  const rest = members.filter((member) => !builtIn.has(member.email.trim().toLowerCase()));
  return [...defaultTeam, ...rest];
}

function cacheTeamMembers(members: TeamMember[]): void {
  try {
    window.localStorage.setItem(teamStorageKey, JSON.stringify(members));
  } catch {
    // The list still holds in memory for this session.
  }
}

/**
 * The last list this device saw. Used for instant rendering and for labelling a
 * session, with {@link loadTeamMembers} refreshing it from the shared database.
 */
export function getTeamMembers(): TeamMember[] {
  try {
    const stored = window.localStorage.getItem(teamStorageKey);
    if (!stored) return defaultTeam;
    const parsed: unknown = JSON.parse(stored);
    return Array.isArray(parsed) && parsed.length ? withDefaultOwners(parsed as TeamMember[]) : defaultTeam;
  } catch {
    return defaultTeam;
  }
}

function rowToMember(row: Record<string, unknown>): TeamMember {
  const email = String(row.email ?? '').trim().toLowerCase();
  return {
    id: email,
    name: String(row.name ?? '') || email,
    email,
    phone: String(row.phone ?? ''),
    role: row.role === 'owner' ? 'owner' : 'manager',
    addedAt: String(row.added_at ?? new Date().toISOString()),
  };
}

/**
 * Reads the access list from the shared database so every device agrees on who
 * can get in. Falls back to this device's last copy if the cloud is unreachable.
 */
export async function loadTeamMembers(): Promise<{ members: TeamMember[]; error?: string }> {
  try {
    // Selecting everything rather than naming columns, so an access list created
    // before the phone column existed still loads instead of erroring.
    const { data, error } = await supabase.from('studio_owners').select('*');
    if (error) throw error;
    const members = withDefaultOwners((data ?? []).map((row) => rowToMember(row as Record<string, unknown>)));
    cacheTeamMembers(members);
    return { members };
  } catch (error) {
    return {
      members: getTeamMembers(),
      error: `Could not reach the shared access list (${readErrorText(error)}). Showing this device's last copy.`,
    };
  }
}

/** Adds someone to the shared access list. Returns an error message, or null. */
export async function addTeamMember(member: {
  name: string;
  email: string;
  phone: string;
  role: TeamRole;
}): Promise<string | null> {
  const email = member.email.trim().toLowerCase();
  if (!email) return 'An email address is required — sign-in links are sent by email.';

  try {
    const { error } = await supabase
      .from('studio_owners')
      .upsert({ email, name: member.name.trim(), phone: member.phone.trim(), role: member.role }, { onConflict: 'email' });
    if (error) throw error;
    return null;
  } catch (error) {
    const raw = readErrorText(error);
    if (/row-level security|policy|denied|permission|401|403/i.test(raw)) {
      return 'Sign in with your emailed link before changing who can log in.';
    }
    if (/phone|added_at|column/i.test(raw)) {
      return `The access list table is missing a column (${raw}). Re-run the latest setup script in Supabase.`;
    }
    return raw ? `Could not add them: ${raw}` : 'Could not add them to the access list.';
  }
}

/** Removes someone from the shared access list. Returns an error message, or null. */
export async function removeTeamMember(email: string): Promise<string | null> {
  const target = email.trim().toLowerCase();
  if (defaultTeam.some((owner) => owner.email === target)) {
    return 'Band owners cannot be removed.';
  }

  try {
    const { error } = await supabase.from('studio_owners').delete().eq('email', target);
    if (error) throw error;
    return null;
  } catch (error) {
    const raw = readErrorText(error);
    if (/row-level security|policy|denied|permission|401|403/i.test(raw)) {
      return 'Sign in with your emailed link before changing who can log in.';
    }
    return raw ? `Could not remove them: ${raw}` : 'Could not remove them from the access list.';
  }
}

/**
 * True only inside the Magic Patterns building environment.
 *
 * The sign-in shortcut is allowed here so the studio stays easy to reach while
 * the site is being built, and disappears automatically once the site is
 * published to its own address.
 */
export function isBuildEnvironment(): boolean {
  try {
    const host = window.location.hostname;
    return (
      host === 'localhost' ||
      host === '127.0.0.1' ||
      host.endsWith('.magicpatterns.app') ||
      host.endsWith('.magicpatterns.com') ||
      host.endsWith('.magicpatterns.dev')
    );
  } catch {
    return false;
  }
}

function matchMember(members: TeamMember[], identifier: string): TeamMember | null {
  const value = identifier.trim().toLowerCase();
  if (!value) return null;
  const digits = value.replace(/\D/g, '');
  return (
    members.find(
      (member) =>
        member.email.trim().toLowerCase() === value ||
        (digits.length >= 10 && member.phone.replace(/\D/g, '').endsWith(digits.slice(-10))),
    ) ?? null
  );
}

/** Matches an email or phone against the people allowed into the studio. */
export function findTeamMember(identifier: string): TeamMember | null {
  return matchMember(getTeamMembers(), identifier);
}

/**
 * Sends a one-tap sign-in link to an email address on the access list.
 *
 * The list is re-read from the shared database first, so someone added on
 * another device can sign in immediately. Supabase delivers the email, so this
 * is real protection: only someone who controls that inbox can open the studio.
 * Returns an error message, or null when the email is on its way.
 */
export async function sendSignInLink(identifier: string): Promise<string | null> {
  const { members } = await loadTeamMembers();
  const member = matchMember(members, identifier);
  if (!member) {
    return 'That email is not on the access list. Ask a band owner to add you in Settings.';
  }
  if (!member.email) {
    return 'That person has no email on file. Sign-in links can only be sent by email.';
  }

  try {
    const { error } = await supabase.auth.signInWithOtp({
      email: member.email,
      options: { emailRedirectTo: studioRedirectUrl() },
    });
    if (error) throw error;
    return null;
  } catch (error) {
    const raw = readErrorText(error);
    if (/signups? not allowed|signup is disabled|not authorized/i.test(raw)) {
      return 'Supabase is refusing to create a sign-in for a first-time address. In Supabase, open Authentication → Sign In / Providers → Email and turn on “Allow new users to sign up”, then try again.';
    }
    if (/rate limit|email rate|over_email_send_rate/i.test(raw)) {
      return 'Supabase’s built-in email sender allows only a couple of messages an hour. Wait an hour, or connect your own email sender under Authentication → Emails → SMTP Settings for unlimited sign-in links.';
    }
    if (/rate|too many|seconds/i.test(raw)) {
      return 'A link was just sent. Wait a minute before asking for another.';
    }
    if (/invalid|valid email/i.test(raw)) {
      return `Supabase rejected that address as invalid (${raw}).`;
    }
    if (/failed to fetch|network/i.test(raw)) {
      return 'No connection. Check your internet and try again.';
    }
    return raw ? `Supabase could not send the link: ${raw}` : 'Could not send the sign-in link.';
  }
}

/**
 * Signs in with an email and password instead of waiting for an emailed link.
 *
 * A stopgap while Supabase's built-in email sender is capped at a couple of
 * messages an hour. It is still a real Supabase sign-in — same session, same
 * database permissions — so saving to the cloud behaves identically to the
 * emailed link. Returns an error message, or null on success.
 */
export async function signInWithPassword(identifier: string, password: string): Promise<string | null> {
  const { members } = await loadTeamMembers();
  const member = matchMember(members, identifier);
  if (!member) {
    return 'That email is not on the access list. Ask a band owner to add you in Settings.';
  }
  if (!password) {
    return 'Enter your password.';
  }

  try {
    const { error } = await supabase.auth.signInWithPassword({ email: member.email, password });
    if (error) throw error;
    return null;
  } catch (error) {
    const raw = readErrorText(error);
    if (/invalid login credentials|invalid credentials/i.test(raw)) {
      return 'That password does not match. If no password has been set for this address yet, open Supabase → Authentication → Users, find the address, and use “Reset password” to set one.';
    }
    if (/email not confirmed/i.test(raw)) {
      return 'Supabase has not confirmed this address yet. In Supabase → Authentication → Users, open the address and confirm it, then try again.';
    }
    if (/failed to fetch|network/i.test(raw)) {
      return 'No connection. Check your internet and try again.';
    }
    return raw ? `Could not sign in: ${raw}` : 'Could not sign in.';
  }
}

/** The email a link was sent to, so the screen can name it back. */
export function describeSignInTarget(identifier: string): string {
  return findTeamMember(identifier)?.email ?? identifier.trim();
}

function sessionFromEmail(email: string): StudioSession {
  const member = getTeamMembers().find((item) => item.email.trim().toLowerCase() === email.trim().toLowerCase());
  return {
    memberId: member?.id ?? email,
    name: member?.name ?? email,
    role: member?.role ?? 'manager',
    signedInAt: new Date().toISOString(),
    expiresAt: Date.now() + 30 * 24 * 60 * 60 * 1000,
  };
}

/** The build-only session, kept separate from a real signed-in session. */
function getSetupSession(): StudioSession | null {
  if (!isBuildEnvironment()) return null;
  try {
    const stored = window.localStorage.getItem(sessionStorageKey);
    if (!stored) return null;
    const session = JSON.parse(stored) as StudioSession;
    if (Date.now() > session.expiresAt) {
      window.localStorage.removeItem(sessionStorageKey);
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

export function startSetupSession(): StudioSession {
  const session: StudioSession = {
    memberId: 'setup-mode',
    name: 'Setup mode',
    role: 'owner',
    signedInAt: new Date().toISOString(),
    expiresAt: Date.now() + setupSessionTtlMs,
  };
  try {
    window.localStorage.setItem(sessionStorageKey, JSON.stringify(session));
  } catch {
    // The session still holds for this tab.
  }
  return session;
}

/** Reads whoever is currently signed in. Checks the cloud, then setup mode. */
export async function loadStudioSession(): Promise<StudioSession | null> {
  try {
    const { data } = await supabase.auth.getSession();
    const email = data.session?.user?.email;
    if (email) {
      // Refresh the shared list so the sidebar shows the right name and role.
      await loadTeamMembers();
      return sessionFromEmail(email);
    }
  } catch {
    // Falls through to setup mode below.
  }
  return getSetupSession();
}

/** Notifies when someone signs in through an emailed link, or signs out. */
export function onStudioSessionChange(handler: (session: StudioSession | null) => void): () => void {
  const { data } = supabase.auth.onAuthStateChange((_event, session) => {
    const email = session?.user?.email;
    handler(email ? sessionFromEmail(email) : getSetupSession());
  });
  return () => data.subscription.unsubscribe();
}

export async function endStudioSession() {
  try {
    window.localStorage.removeItem(sessionStorageKey);
  } catch {
    // Nothing to clean up.
  }
  try {
    await supabase.auth.signOut();
  } catch {
    // Already signed out.
  }
}
